import { useDispatch, useSelector } from "react-redux"
import { getIsLoggedIn } from "#/redux/reducers/application"
import React, { useState } from "react"
import actionTypes from "#/redux/actions/actionTypes"
import { CarrotErrorCodes } from "../../../utils/CarrotErrors"
import { useQueryParam } from "use-query-params"
import { signOut } from "#/redux/actions/loginActions"
import { useHistory } from "react-router"
// Deep import on purpose: `@internal/consumer` is lazily loaded (see Root.tsx), and
// importing from the package ROOT here would pull that whole chunk into the main
// bundle. The `./funnel-storage` subpath is a dependency-free module, declared in
// the package's `exports` map — verified in the production build: the main entry
// carries only that module while the funnel UI stays in its own chunk.
import { clearPersistedFunnel } from "@internal/consumer/funnel-storage"

const DELAY_INDEX_PAGE_RETURN = 200

type AuthenticationContextReturn = {
  isLoggedIn: boolean
  handleSessionExpiration: () => void
  isExplicitLogout: boolean
  handleSignOut: () => void
  resetExplicitLogout: () => void
  handleSignOutNoRedirect: () => void
}

interface AuthenticationProviderProps {
  children: React.ReactNode | React.ReactNode[]
}

const AuthenticationContext = React.createContext<AuthenticationContextReturn>(null)

export const useAuthentication = (): AuthenticationContextReturn => React.useContext(AuthenticationContext)

function UserSessionBoundary({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

export function AuthenticationProvider({ children }: AuthenticationProviderProps): JSX.Element {
  const [_errorCode, setErrorCode] = useQueryParam("errorCode")
  const history = useHistory()
  const [isExplicitLogout, setIsExplicitLogout] = useState(false)
  const isLoggedIn = useSelector(getIsLoggedIn)
  const dispatch = useDispatch()

  const handleSessionExpiration = React.useCallback(() => {
    // Third path across the same authentication boundary as the two sign-out handlers
    // below, so it gets the same eviction: an expired session leaves the member logged
    // out with the tab intact, and without this the previous member's funnel PII/PHI
    // stays readable in `sessionStorage` to whoever uses the device next. The cost is
    // that a signed-in member whose session expires MID-funnel loses their in-progress
    // details and has to re-enter them after signing back in — accepted deliberately:
    // the boundary changed, which is exactly when the data should go.
    clearPersistedFunnel()
    dispatch({
      type: actionTypes.SESSION_EXPIRED
    })
    setErrorCode(CarrotErrorCodes.NOT_AUTHENTICATED)
  }, [dispatch, setErrorCode])

  const resetExplicitLogout = React.useCallback(() => {
    setIsExplicitLogout(false)
  }, [])

  const handleSignOut = React.useCallback(() => {
    setIsExplicitLogout(true)
    // Sign-out resets Redux in memory but not web storage, so the subscription
    // funnel's PII/PHI blob would otherwise survive in this tab for whoever uses
    // the device next. Cleared here rather than in the consumer package because
    // the funnel is not mounted at sign-out time.
    clearPersistedFunnel()
    // @ts-expect-error TS(7006) FIXME: Parameter 'internalDispatch' implicitly has an 'an... Remove this comment to see the full error message
    dispatch(async (internalDispatch) => {
      await signOut()(internalDispatch)
      // To prevent flashing of logged in Home Screen
      setTimeout(() => history.push("/"), DELAY_INDEX_PAGE_RETURN)
    })
  }, [dispatch, history])

  const handleSignOutNoRedirect = React.useCallback(() => {
    setIsExplicitLogout(true)
    // Same funnel-storage eviction as `handleSignOut` above.
    clearPersistedFunnel()
    // @ts-expect-error TS(7006) FIXME: Parameter 'internalDispatch' implicitly has an 'an... Remove this comment to see the full error message
    dispatch(async (internalDispatch) => {
      await signOut()(internalDispatch)
    })
  }, [dispatch])

  return (
    <AuthenticationContext.Provider
      value={{
        isLoggedIn,
        isExplicitLogout,
        handleSessionExpiration,
        handleSignOut,
        resetExplicitLogout,
        handleSignOutNoRedirect
      }}
    >
      <UserSessionBoundary key={isLoggedIn ? "logged-in" : "logged-out"}>{children}</UserSessionBoundary>
    </AuthenticationContext.Provider>
  )
}
